import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { Icon, type IconName } from './Icon';
import { Modal } from './ui';
import { useToast } from './Toast';

/**
 * كتابة رسالة من داخل النظام — نافذة عائمة كمنشئ Gmail.
 *
 * تُفتح من أي مكان: أيقونة البريد في صفّ عميل أو جهة اتصال تفتحها مملوءةً
 * بعنوانه، وزرّ «رسالة جديدة» في البريد يفتحها فارغة، والردّ يفتحها على
 * الخيط نفسه.
 *
 * ## ولماذا نافذة عائمة لا Modal
 *
 * الرسالة تُكتب والعين على ما يُكتب عنه: ملفّ العميل وأرقامه وسجلّه.
 * وModal يحجب الشاشة كلها فيُجبر الكاتب على الحفظ في ذاكرته ما كان أمامه.
 * النافذة تلتصق بأسفل الشاشة والصفحة خلفها حيّة تُقرأ وتُمرَّر — وتُطوى
 * بنقرة فيبقى عنوانها حاضرًا حتى يعود إليها.
 *
 * ## ولماذا لا `mailto:`
 *
 * `mailto:` يفتح برنامج بريد الجهاز — إن وُجد — فتخرج الرسالة من حساب
 * الموظف الشخصي ولا يعلم بها النظام. والإرسال من هنا يمرّ على صندوق
 * الشركة ويُحفظ مربوطًا بالسجل.
 *
 * ## والتنسيق عبر `contentEditable`
 *
 * الخادم يقبل `html`، فالعريض والمائل والقوائم تصل كما كُتبت. والاقتباس في
 * الردّ يُبنى من النصّ الخام مهرَّبًا لا من `bodyHtml` الوارد — ذاك كتبه
 * الغريب، وحقنه في محرّرنا يشغّل ما حمل من معالجات أحداث.
 */

interface MailAccount {
  id: string;
  label: string;
  address: string;
  isActive: boolean;
  /** توقيع الصندوق — يُذيَّل تلقائيًّا. */
  signatureHtml?: string | null;
}

/** السجل الذي تُنسب إليه الرسالة، فتدخل تاريخه. */
export interface MailLink {
  accountRefId?: string | null;
  contactId?: string | null;
  leadId?: string | null;
  dealId?: string | null;
}

/** الرسالة المردود عليها — ما يلزم للخيط والاقتباس فقط. */
export interface ReplySource {
  id: string;
  fromAddress: string;
  fromName?: string | null;
  subject: string;
  sentAt: string;
  bodyText?: string | null;
  /** صندوق الوصول — الردّ يخرج منه لا من أول صندوق. */
  mailAccountId?: string;
  accountRefId?: string | null;
  contactId?: string | null;
  leadId?: string | null;
}

export function MailCompose({
  to = '',
  link,
  subject: initialSubject = '',
  body: initialBody,
  replyTo,
  onClose,
  onSent,
}: {
  to?: string;
  link?: MailLink;
  subject?: string;
  /** متن ابتدائي — تمهّد به قوالبُ الرسائل نصَّها المملوء. */
  body?: string;
  replyTo?: ReplySource | null;
  onClose: () => void;
  onSent?: () => void;
}) {
  const toast = useToast();
  const editor = useRef<HTMLDivElement>(null);

  const accounts = useQuery({
    queryKey: ['mail', 'accounts'],
    queryFn: () => api<{ items: MailAccount[] }>('/mail/accounts'),
  });

  const usable = (accounts.data?.items ?? []).filter((a) => a.isActive);

  const [accountId, setAccountId] = useState(replyTo?.mailAccountId ?? '');
  const [recipients, setRecipients] = useState(replyTo ? replyTo.fromAddress : to);
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState(
    replyTo
      ? replyTo.subject.startsWith('رد:')
        ? replyTo.subject
        : `رد: ${replyTo.subject}`
      : initialSubject,
  );
  const [minimized, setMinimized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* أول صندوق فعّال يُختار تلقائيًّا حين تصل القائمة. */
  const chosen = accountId || usable[0]?.id || '';

  /*
   * اقتباس الردّ يُزرع مرة عند الفتح.
   *
   * `contentEditable` يملك محتواه بعدها — إعادة الكتابة من الحالة مع كل
   * تصيير تفقد موضع المؤشّر وما كُتب.
   */
  useEffect(() => {
    const el = editor.current;
    if (!el) return;
    if (!replyTo && initialBody) {
      /* القالب نصٌّ داخلي يُهرَّب كأي نص — لا HTML يُحقن. */
      el.innerHTML = escapeHtml(initialBody).replace(/\n/g, '<br>');
    }
    if (replyTo) {
      const quoted = escapeHtml(replyTo.bodyText ?? '').replace(/\n/g, '<br>');
      el.innerHTML =
        '<br><br><div class="goyone-quote"><div style="color:#6f7784;font-size:.85em">' +
        `في ${escapeHtml(dateTime(replyTo.sentAt))}، كتب ${escapeHtml(
          replyTo.fromName || replyTo.fromAddress,
        )}:</div>` +
        '<blockquote style="border-inline-start:2px solid #d5dae2;margin:4px 0 0;padding-inline-start:12px;color:#6f7784">' +
        quoted +
        '</blockquote></div>';
    }
    el.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * التوقيع يُذيَّل من إعدادات الصندوق، ويُستبدل حين يتبدّل الصندوق.
   *
   * وموضعه قبل الاقتباس في الرد لا بعده: ما بعد الاقتباس لا يقرؤه أحد.
   * والكتلة مُعلَّمة بفئةٍ كي يُنزع القديم عند التبديل لا أن يتراكم.
   */
  useEffect(() => {
    const el = editor.current;
    if (!el) return;
    el.querySelector('.goyone-sig')?.remove();
    const acc = usable.find((a) => a.id === chosen);
    if (!acc?.signatureHtml) return;
    const sig = document.createElement('div');
    sig.className = 'goyone-sig';
    sig.innerHTML = '<br><div style="color:#6f7784">--</div>' + acc.signatureHtml;
    const quote = el.querySelector('.goyone-quote');
    if (quote) el.insertBefore(sig, quote);
    else el.appendChild(sig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, accounts.data]);

  const send = useMutation({
    mutationFn: () => {
      const el = editor.current;
      return api('/mail/send', {
        method: 'POST',
        body: {
          accountId: chosen,
          to: splitAddresses(recipients),
          cc: splitAddresses(cc),
          subject: subject.trim(),
          text: el?.innerText ?? '',
          html: el?.innerHTML || undefined,
          replyToId: replyTo?.id,
          accountRefId: replyTo?.accountRefId ?? link?.accountRefId ?? undefined,
          contactId: replyTo?.contactId ?? link?.contactId ?? undefined,
          leadId: replyTo?.leadId ?? link?.leadId ?? undefined,
          dealId: link?.dealId ?? undefined,
        },
      });
    },
    onSuccess: () => {
      toast('أُرسلت الرسالة وحُفظت في سجل العميل');
      onSent?.();
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  const valid = chosen && splitAddresses(recipients).length > 0 && subject.trim().length > 0;

  const trySend = () => {
    if (!valid || send.isPending) return;
    setError(null);
    send.mutate();
  };

  /* ---------- لا صندوق مربوط ---------- */
  if (accounts.isSuccess && usable.length === 0) {
    return (
      <Modal title="لا صندوق بريد مربوط" onClose={onClose}>
        <p className="small">
          الإرسال من داخل النظام يحتاج صندوق بريد للشركة — به تُحفظ الرسالة في سجل
          العميل ويعرف من يفتح ملفّه أنك راسلته.
        </p>

        <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
          <Link to="/settings/mail" className="btn btn-primary" onClick={onClose}>
            <Icon name="settings" size={15} />
            اربط صندوقًا
          </Link>
          {/*
            المخرج اليدوي يبقى: من يريد أن يراسله الآن يفتح برنامجه —
            ويعلم أن الرسالة لن تُحفظ هنا.
          */}
          <a className="btn" href={`mailto:${to}`} onClick={onClose}>
            افتح برنامج البريد بدلًا
          </a>
        </div>
      </Modal>
    );
  }

  return (
    <div
      className={`composer${minimized ? ' composer-min' : ''}`}
      role="dialog"
      aria-label="كتابة رسالة"
      onKeyDown={(e) => {
        /* كما في Gmail: Ctrl+Enter يُرسل وEsc يغلق. */
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          trySend();
        }
        if (e.key === 'Escape') onClose();
      }}
    >
      {/* الترويسة تطوي وتغلق — والنقر عليها وهي مطوية يفتحها. */}
      <div
        className="composer-head"
        onClick={() => {
          if (minimized) setMinimized(false);
        }}
      >
        <span className="composer-title">
          {subject.trim() || (replyTo ? 'رد على الرسالة' : 'رسالة جديدة')}
        </span>
        <span className="row" style={{ gap: 2 }}>
          <button
            type="button"
            className="composer-head-btn"
            onClick={(e) => {
              e.stopPropagation();
              setMinimized((m) => !m);
            }}
            title={minimized ? 'توسيع' : 'طيّ'}
            aria-label={minimized ? 'توسيع' : 'طيّ'}
          >
            <Icon name={minimized ? 'chevronUp' : 'minus'} size={14} />
          </button>
          <button
            type="button"
            className="composer-head-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="إغلاق"
            aria-label="إغلاق"
          >
            <Icon name="x" size={14} />
          </button>
        </span>
      </div>

      {/*
        المطويّة تُخفى ولا تُفكّ من الشجرة.

        محتوى `contentEditable` يسكن الـDOM لا الحالة، وفكُّه يعني أن من طوى
        رسالته ليقرأ شيئًا في الصفحة يعود فيجدها بيضاء.
      */}
      <div className="composer-panes" hidden={minimized}>
          {error && (
            <div className="alert alert-danger" style={{ margin: 'var(--sp-2) var(--sp-3) 0' }}>
              {error}
            </div>
          )}

          {accounts.isPending ? (
            <div className="skeleton" style={{ height: 60, margin: 'var(--sp-3)' }} />
          ) : (
            <>
              {/* الصندوق المُرسِل — يُختار حين يكون أكثر من واحد. */}
              {usable.length > 1 ? (
                <div className="composer-field">
                  <span className="composer-field-label">من</span>
                  <select
                    className="composer-input"
                    value={chosen}
                    onChange={(e) => setAccountId(e.target.value)}
                  >
                    {usable.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label} — {a.address}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                usable[0] && (
                  <div className="composer-field composer-from">
                    <span className="composer-field-label">من</span>
                    <span className="num tiny">{usable[0].address}</span>
                  </div>
                )
              )}

              <div className="composer-field">
                <span className="composer-field-label">إلى</span>
                <input
                  className="composer-input"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  dir="ltr"
                  placeholder="name@example.com"
                />
                {!showCc && (
                  <button
                    type="button"
                    className="composer-cc-toggle"
                    onClick={() => setShowCc(true)}
                  >
                    نسخة
                  </button>
                )}
              </div>

              {showCc && (
                <div className="composer-field">
                  <span className="composer-field-label">نسخة</span>
                  <input
                    className="composer-input"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    dir="ltr"
                    autoFocus
                  />
                </div>
              )}

              <div className="composer-field">
                <input
                  className="composer-input composer-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="الموضوع"
                  aria-label="الموضوع"
                />
              </div>

              {/*
                dir="auto": رسالة عربية تنساب يمينًا ورسالة إنجليزية يسارًا،
                والحكم للسطر الأول كما في كل بريد.
              */}
              <div
                ref={editor}
                className="composer-body"
                contentEditable
                dir="auto"
                data-placeholder="اكتب رسالتك…"
                aria-label="نص الرسالة"
              />

              <div className="composer-foot">
                <button
                  type="button"
                  className="btn btn-primary composer-send"
                  onClick={trySend}
                  disabled={!valid || send.isPending}
                  title="Ctrl+Enter"
                >
                  <Icon name="send" size={15} />
                  {send.isPending ? 'جارٍ الإرسال' : 'إرسال'}
                </button>

                <span className="composer-tools" role="toolbar" aria-label="تنسيق النص">
                  <ToolButton icon="bold" title="عريض" cmd="bold" editor={editor} />
                  <ToolButton icon="italic" title="مائل" cmd="italic" editor={editor} />
                  <ToolButton icon="underline" title="تسطير" cmd="underline" editor={editor} />
                  <ToolButton
                    icon="listUl"
                    title="قائمة نقطية"
                    cmd="insertUnorderedList"
                    editor={editor}
                  />
                  <ToolButton
                    icon="listOl"
                    title="قائمة مرقّمة"
                    cmd="insertOrderedList"
                    editor={editor}
                  />
                  <ToolButton icon="link" title="رابط" cmd="createLink" editor={editor} />
                  <ToolButton
                    icon="eraser"
                    title="إزالة التنسيق"
                    cmd="removeFormat"
                    editor={editor}
                  />
                </span>

                <button
                  type="button"
                  className="composer-head-btn composer-discard"
                  onClick={onClose}
                  title="تجاهل الرسالة"
                  aria-label="تجاهل الرسالة"
                >
                  <Icon name="trash" size={15} />
                </button>
              </div>

              <div className="tiny mute" style={{ padding: '0 var(--sp-3) var(--sp-2)' }}>
                تُحفظ في سجل العميل بعد الإرسال. Ctrl+Enter للإرسال السريع.
              </div>
            </>
          )}
      </div>
    </div>
  );
}

/**
 * زرّ تنسيق واحد.
 *
 * `onMouseDown` مع منع الافتراضي لا `onClick`: النقر يسحب التحديد من
 * المحرّر قبل أن يصل الأمر، فيقع التنسيق على لا شيء.
 */
function ToolButton({
  icon,
  title,
  cmd,
  editor,
}: {
  icon: IconName;
  title: string;
  cmd: string;
  editor: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <button
      type="button"
      className="composer-tool"
      title={title}
      aria-label={title}
      onMouseDown={(e) => {
        e.preventDefault();
        editor.current?.focus();
        if (cmd === 'createLink') {
          const url = window.prompt('عنوان الرابط:', 'https://');
          if (!url || url === 'https://') return;
          document.execCommand(cmd, false, url);
          return;
        }
        document.execCommand(cmd, false);
      }}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}

/**
 * العناوين تُفصل بفاصلة عربية أو لاتينية أو مسافة.
 *
 * من ينسخ عناوين من جدول أو من رسالة يحصل عليها بأي منها، ورفضُ ما لا
 * يطابق فاصلةً واحدة يجعله يصحّح بيده ما كان النظام أولى بتصحيحه.
 */
function splitAddresses(raw: string): string[] {
  return raw
    .split(/[,،;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** تهريب نصٍّ خام قبل زرعه في HTML — الاقتباس كتبه الطرف الآخر. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
